import {create} from 'zustand';
import {devtools} from 'zustand/middleware';
import {immer} from 'zustand/middleware/immer';
import {FeatureName} from '../types/project';

// Sorting options
export enum ProjectsSortField {
  UPDATED_AT = 'updatedAt',
  NAME = 'name',
  CREATED_AT = 'createdAt',
}

export enum ProjectSortField {
  STATUS = 'status',
  FILE_NAME = 'fileName',
  LANGUAGE = 'language',
  ASPECT_RATIO = 'aspectRatio',
  RESOLUTION = 'resolution',
}

export type SortDirection = 'asc' | 'desc';

export type ProjectsSorting = {
  field: ProjectsSortField;
  direction: SortDirection;
};

export enum ProjectsToolFilter {
  ALL = 'all',
  RESIZE = FeatureName.REFORMATTING,
  DUB = FeatureName.AI_DUBBING,
  REPHRASE = FeatureName.REPHRASING,
  CAPTIONS = FeatureName.CAPTIONS,
  AI_AVATAR = FeatureName.AI_AVATAR,
  UPSCALE = FeatureName.UPSCALE,
}

export enum ProjectsStatusFilter {
  ALL = 'all',
  READY_TO_USE = 'ready_to_use',
  IN_PROGRESS = 'in_progress',
}

export type ProjectsFilters = {
  tool: ProjectsToolFilter;
  status: ProjectsStatusFilter;
};

export type ProjectPageSorting = {
  field: ProjectSortField;
  direction: SortDirection;
};

export type ProjectsStoreState = {
  sorting: ProjectsSorting;
  filters: ProjectsFilters;
  projectPageSorting: Record<string, ProjectPageSorting>;
};

const defaultSorting: ProjectsSorting = {
  field: ProjectsSortField.UPDATED_AT,
  direction: 'desc',
};

const initialState: ProjectsStoreState = {
  sorting: defaultSorting,
  filters: {
    tool: ProjectsToolFilter.ALL,
    status: ProjectsStatusFilter.ALL,
  },
  projectPageSorting: {},
};

const projectsStore = create<ProjectsStoreState>()(
  devtools(immer(() => initialState))
);

// Actions
const setProjectsSorting = (sorting: ProjectsSorting) =>
  projectsStore.setState((state) => {
    state.sorting = sorting;
  });

const setProjectsFilters = (filters: Partial<ProjectsFilters>) =>
  projectsStore.setState((state) => {
    state.filters = {
      ...state.filters,
      ...filters,
    };
  });

const setProjectPageDefaultSorting = (projectId: string) =>
  projectsStore.setState((state) => {
    if (!state.projectPageSorting[projectId]) {
      state.projectPageSorting[projectId] = {
        field: ProjectSortField.STATUS,
        direction: 'desc',
      };
    }
  });

const setProjectPageSorting = (
  projectId: string,
  sorting: ProjectPageSorting
) =>
  projectsStore.setState((state) => {
    state.projectPageSorting[projectId] = sorting;
  });

const setProjectsDefaultSorting = () =>
  projectsStore.setState((state) => {
    state.sorting = defaultSorting;
  });

const clearProjectPageSorting = (projectId: string) =>
  projectsStore.setState((state) => {
    delete state.projectPageSorting[projectId];
  });

const resetProjectsStore = () => projectsStore.setState(() => initialState);

export const projectsStoreActions = {
  setProjectsSorting,
  setProjectsFilters,
  setProjectPageDefaultSorting,
  setProjectPageSorting,
  setProjectsDefaultSorting,
  clearProjectPageSorting,
  resetProjectsStore,
};

export default projectsStore;
